import { Controller } from "@hotwired/stimulus"

/**
 * Экспорт workspace — карта экспорта.
 * Макет аналогичен import_map_controller, но:
 *   - нет статусов (new/update/unchanged) — все элементы «доступны для экспорта»
 *   - inventory грузится через GET .../export/inventory
 *   - selection отправляется в POST /export (как hidden inputs selection[<group>][])
 */

const ICONS = {
  workspace: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="14" rx="2" stroke="currentColor" stroke-width="1.6"/><path d="M3 9h18" stroke="currentColor" stroke-width="1.6"/></svg>`,
  folders:   `<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M3 6a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6z" stroke="currentColor" stroke-width="1.6"/></svg>`,
  sources:   `<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><ellipse cx="12" cy="5" rx="8" ry="3" stroke="currentColor" stroke-width="1.6"/><path d="M4 5v7c0 1.7 3.6 3 8 3s8-1.3 8-3V5M4 12v7c0 1.7 3.6 3 8 3s8-1.3 8-3v-7" stroke="currentColor" stroke-width="1.6"/></svg>`,
  item_templates: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="4" stroke="currentColor" stroke-width="1.6"/><rect x="3" y="12" width="8" height="8" stroke="currentColor" stroke-width="1.6"/><rect x="13" y="12" width="8" height="8" stroke="currentColor" stroke-width="1.6"/></svg>`,
  pages:     `<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><rect x="4" y="3" width="16" height="18" stroke="currentColor" stroke-width="1.6"/><path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`,
  components:`<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" stroke="currentColor" stroke-width="1.6"/><path d="M12 3v18M4 7.5l16 9M20 7.5l-16 9" stroke="currentColor" stroke-width="1.2" opacity=".4"/></svg>`,
  lists:     `<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M4 6h16M4 12h16M4 18h10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`
}

const GROUPS_ORDER = [
  { key: "pages",          title: "Страницы",       hint: "Страницы этого workspace" },
  { key: "components",     title: "Компоненты",     hint: "UI-блоки страниц" },
  { key: "item_templates", title: "Item Templates", hint: "Все шаблоны в системе" },
  { key: "sources",        title: "Источники",      hint: "Все источники в системе" },
  { key: "folders",        title: "Папки",          hint: "Папки sources/templates" },
  { key: "lists",          title: "Lists",          hint: "Справочники / enum-ы" }
]

export default class extends Controller {
  static targets = [
    "loadBtn", "inventoryIndicator", "inventoryIndicatorText",
    "mainGrid", "emptyState", "groupsPane", "groupsList", "totalsCard", "detailPane",
    "selectionBar", "selectionCount", "selectionBreakdown"
  ]
  static values = { inventoryUrl: String }

  connect() {
    this.inventory = null
    this.activeKey = localStorage.getItem("ex_active") || "pages"
    this.selection = {}
    this.workspaceSelection = new Set()
    this.queries = {}
    GROUPS_ORDER.forEach(g => {
      this.selection[g.key] = new Set()
      this.queries[g.key] = ""
    })
  }

  // ═════════════════════════════════════════════════════════════════
  // LOAD INVENTORY
  // ═════════════════════════════════════════════════════════════════

  async loadInventory(event) {
    event.preventDefault()
    this.loadBtnTarget.disabled = true
    const original = this.loadBtnTarget.innerHTML
    this.loadBtnTarget.innerHTML = "Загрузка..."
    try {
      const response = await fetch(this.inventoryUrlValue, {
        headers: { "Accept": "application/json" }
      })
      const data = await response.json()
      if (!data.success) {
        this._showError((data.errors || ["Ошибка загрузки инвентаря"]).join(", "))
        return
      }
      this.inventory = data
      this._seedDefaultSelection()
      this._renderAll()
      this._showIndicator()
    } catch (e) {
      this._showError(`Ошибка: ${e.message}`)
    } finally {
      this.loadBtnTarget.disabled = false
      this.loadBtnTarget.innerHTML = original
    }
  }

  _showIndicator() {
    const t = this._computeTotals()
    this.inventoryIndicatorTextTarget.textContent = `Найдено ${t.total} элементов`
    this.inventoryIndicatorTarget.classList.remove("hidden")
    this.inventoryIndicatorTarget.classList.add("inline-flex")
  }

  _seedDefaultSelection() {
    // По умолчанию отмечаем: все pages + все components текущего workspace.
    // Источники/шаблоны/lists/folders — опционально (по клику user-а).
    const pages = this.inventory?.pages || []
    const components = this.inventory?.components || []
    this.selection.pages = new Set(pages.map(p => p.code))
    this.selection.components = new Set(components.map(c => c.code))
    GROUPS_ORDER.forEach(g => {
      if (g.key !== "pages" && g.key !== "components") {
        this.selection[g.key] = new Set()
      }
    })
    // Workspace-секции: запись WS по-умолчанию включена; остальные — если есть в payload
    this.workspaceSelection = new Set(["workspace"])
    if (this.inventory.workspace_llm_settings_present) this.workspaceSelection.add("workspace_llm_settings")
    if (this.inventory.workspace_settings_present) this.workspaceSelection.add("workspace_settings")
  }

  // ═════════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════════

  _renderAll() {
    this.emptyStateTarget.classList.add("hidden")
    this.groupsPaneTarget.classList.remove("hidden")
    this.groupsPaneTarget.classList.add("flex", "flex-col")
    this.detailPaneTarget.classList.remove("hidden")
    this.detailPaneTarget.classList.add("flex")
    this._renderGroupsPane()
    this._renderTotalsCard()
    this._renderDetail()
    this._renderSelectionBar()
  }

  _renderGroupsPane() {
    const list = this.groupsListTarget
    list.innerHTML = ""

    // Workspace первым.
    list.appendChild(this._buildGroupRow({
      key: "workspace",
      title: "Workspace",
      icon: ICONS.workspace,
      total: this._workspaceTotal(),
      selected: this.workspaceSelection.size
    }))

    list.appendChild(this._el("div", "", "", { style: "height: 10px" }))
    list.appendChild(this._el("div",
      "px-0.5 py-1 text-[10.5px] font-semibold uppercase tracking-wider text-[#9AA1AC]",
      "Связанные сущности"))

    GROUPS_ORDER.forEach(g => {
      const items = this.inventory?.[g.key] || []
      list.appendChild(this._buildGroupRow({
        key: g.key,
        title: g.title,
        icon: ICONS[g.key] || ICONS.components,
        total: items.length,
        selected: this.selection[g.key].size
      }))
    })
  }

  _buildGroupRow({ key, title, icon, total, selected }) {
    const active = this.activeKey === key
    const hasSel = selected > 0
    const allSel = selected === total && total > 0

    const btn = document.createElement("button")
    btn.type = "button"
    btn.dataset.action = "click->dae20--workspace-export-map#setActiveGroup"
    btn.dataset.groupKey = key
    btn.className = [
      "relative grid w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-all",
      active
        ? "bg-white border border-[#E6E8EC] shadow-[0_1px_0_rgba(16,24,40,0.04),0_1px_2px_rgba(16,24,40,0.04)]"
        : "border border-transparent hover:bg-black/[0.03]"
    ].join(" ")
    btn.style.gridTemplateColumns = "22px 1fr auto"

    const activeMark = active
      ? `<span class="absolute -left-3.5 top-1.5 bottom-1.5 w-[3px] rounded bg-[#3D5AFE]"></span>`
      : ""
    const iconColor = active ? "text-[#3D5AFE]" : "text-[#6B7280]"

    const selBadge = hasSel
      ? `<span class="font-mono text-[10px] font-bold px-1.5 py-[1px] rounded
                     ${allSel ? 'bg-[#3D5AFE] text-white' : 'bg-[#EEF0FF] text-[#2540E0]'}">
          ${selected}/${total}
        </span>`
      : ""

    btn.innerHTML = `
      ${activeMark}
      <span class="flex items-center justify-center ${iconColor}">${icon}</span>
      <span class="flex flex-col gap-0.5 min-w-0">
        <span class="font-semibold text-[13px] text-[#0E1116]">${this._esc(title)}</span>
      </span>
      <span class="flex flex-col items-end gap-0.5">
        <span class="font-mono text-[11px] font-semibold text-[#6B7280]">${total}</span>
        ${selBadge}
      </span>
    `
    return btn
  }

  _renderTotalsCard() {
    const t = this._computeTotals()
    const sel = this._totalSelected()
    this.totalsCardTarget.innerHTML = `
      <div class="flex items-center justify-between mb-2">
        <span class="font-semibold text-[#394150]">Всего в системе</span>
        <span class="font-mono font-bold text-[#0E1116] text-[14px]">${t.total}</span>
      </div>
      <div class="flex items-center justify-between text-[12px] text-[#6B7280] mb-1">
        <span>Выбрано для экспорта</span>
        <span class="font-mono font-bold text-[#2540E0]">${sel}</span>
      </div>
      <div class="flex h-1.5 rounded overflow-hidden bg-[#EFF1F4]">
        <div style="width:${t.total > 0 ? (sel/t.total)*100 : 0}%; background:#3D5AFE;"></div>
      </div>
    `
  }

  _renderDetail() {
    const pane = this.detailPaneTarget
    pane.innerHTML = ""
    pane.className = "flex-1 flex flex-col bg-white min-w-0"

    if (this.activeKey === "workspace") {
      pane.appendChild(this._buildWorkspaceDetail())
    } else {
      pane.appendChild(this._buildGroupDetail(this.activeKey))
    }
    this._syncHiddenSelectionInputs()
  }

  _buildWorkspaceDetail() {
    const wrap = document.createElement("div")
    wrap.className = "flex flex-col h-full"

    const blocks = [
      {
        id: "workspace",
        label: "Запись Workspace",
        hint: "name, description, group_name",
        present: true
      },
      {
        id: "workspace_llm_settings",
        label: "LLM-настройки",
        hint: "provider, model, temperature",
        present: !!this.inventory.workspace_llm_settings_present
      },
      {
        id: "workspace_settings",
        label: "Тема / Welcome / Announcements / Widgets",
        hint: "theme tokens, welcome, widgets",
        present: !!this.inventory.workspace_settings_present
      }
    ]

    const header = document.createElement("div")
    header.className = "px-6 py-3.5 border-b border-[#E6E8EC] flex items-center justify-between gap-3"
    header.innerHTML = `
      <div>
        <div class="flex items-center gap-2.5 mb-1">
          <span class="text-[17px] font-bold">Workspace:
            <span class="font-mono text-[#2540E0]">${this._esc(this.inventory.workspace?.code || "")}</span>
          </span>
        </div>
        <div class="text-[12.5px] text-[#6B7280]">
          Секции workspace, которые войдут в экспорт. «Не в payload» — соответствующие данные не настроены.
        </div>
      </div>
      <div class="flex gap-1.5">
        <button type="button" data-action="click->dae20--workspace-export-map#workspaceSelectAll" class="${this._softBtnClass()}">Все</button>
        <button type="button" data-action="click->dae20--workspace-export-map#workspaceSelectNone" class="${this._softBtnClass()}">Никого</button>
      </div>
    `
    wrap.appendChild(header)

    const body = document.createElement("div")
    body.className = "flex-1 overflow-y-auto py-2"

    blocks.forEach(b => {
      const checked = this.workspaceSelection.has(b.id)
      const row = document.createElement("div")
      row.dataset.action = "click->dae20--workspace-export-map#toggleWorkspaceBlock"
      row.dataset.blockId = b.id
      row.className = [
        "grid items-center gap-3 px-6 py-3 border-b border-[#EFF1F4] cursor-pointer transition-colors",
        checked ? "bg-[rgba(61,90,254,0.05)] border-l-2 border-l-[#3D5AFE]" : "border-l-2 border-l-transparent hover:bg-black/[0.025]",
        !b.present ? "opacity-50" : ""
      ].join(" ")
      row.style.gridTemplateColumns = "28px 1fr auto"

      row.innerHTML = `
        <div onclick="event.stopPropagation()">
          <input type="checkbox" ${checked ? "checked" : ""} ${!b.present ? "disabled" : ""}
                 class="cursor-pointer"
                 style="width:15px; height:15px; accent-color:#3D5AFE;"
                 data-action="change->dae20--workspace-export-map#toggleWorkspaceCheckbox"
                 data-block-id="${b.id}">
        </div>
        <div class="flex flex-col gap-0.5 min-w-0">
          <span class="font-semibold text-[13.5px]">${this._esc(b.label)}</span>
          <span class="font-mono text-[11.5px] text-[#6B7280]">${this._esc(b.hint)}${b.present ? "" : " · (не в payload)"}</span>
        </div>
        <div></div>
      `
      body.appendChild(row)
    })

    wrap.appendChild(body)
    return wrap
  }

  _buildGroupDetail(groupKey) {
    const meta = GROUPS_ORDER.find(g => g.key === groupKey)
    const items = this.inventory?.[groupKey] || []
    const query = (this.queries[groupKey] || "").toLowerCase()

    const filtered = items.filter(it => {
      if (!query) return true
      const code = (it.code || "").toLowerCase()
      const name = (it.name || "").toLowerCase()
      return code.includes(query) || name.includes(query)
    })

    const wrap = document.createElement("div")
    wrap.className = "flex flex-col h-full"

    const header = document.createElement("div")
    header.className = "px-6 pt-3.5 pb-3 border-b border-[#E6E8EC]"
    header.innerHTML = `
      <div class="flex items-center justify-between gap-3 mb-3">
        <div class="flex items-center gap-3">
          <span class="text-[#6B7280]">${ICONS[groupKey] || ""}</span>
          <span class="text-[17px] font-bold">${this._esc(meta.title)}</span>
          <span class="font-mono text-[13px] font-semibold text-[#6B7280]">${items.length}</span>
          <span class="text-[12.5px] text-[#6B7280]">· ${this._esc(meta.hint)}</span>
        </div>
        <div class="flex gap-1.5">
          <button type="button" data-action="click->dae20--workspace-export-map#groupSelectAll"      data-group-key="${groupKey}" class="${this._softBtnClass()}">Все</button>
          <button type="button" data-action="click->dae20--workspace-export-map#groupSelectNone"     data-group-key="${groupKey}" class="${this._softBtnClass()}">Никого</button>
        </div>
      </div>
      <div class="flex items-center gap-2.5">
        <div class="relative flex-[0_1_360px]">
          <span class="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9AA1AC]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2"/><path d="m20 20-3.5-3.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          </span>
          <input type="text"
                 placeholder="Поиск по коду или названию…"
                 value="${this._esc(this.queries[groupKey] || '')}"
                 data-action="input->dae20--workspace-export-map#onQueryChange"
                 data-group-key="${groupKey}"
                 class="w-full h-8 pl-8 pr-3 border border-[#E6E8EC] rounded-lg text-[13px] bg-white outline-none focus:border-[#3D5AFE] focus:shadow-[0_0_0_3px_rgba(61,90,254,0.22)]">
        </div>
        <span class="text-[12px] text-[#6B7280]">выбрано <span class="font-mono font-semibold text-[#2540E0]">${this.selection[groupKey].size}</span> / ${items.length}</span>
      </div>
    `

    const tableHeader = document.createElement("div")
    tableHeader.className = "grid items-center gap-3 px-3.5 py-2 text-[10.5px] font-semibold uppercase tracking-wider text-[#9AA1AC] bg-[#FBFBFD] border-b border-[#E6E8EC]"
    tableHeader.style.gridTemplateColumns = "28px 1fr auto"

    const allVisibleChecked = filtered.length > 0 && filtered.every(it => this.selection[groupKey].has(it.code))
    const someVisible = filtered.some(it => this.selection[groupKey].has(it.code))

    tableHeader.innerHTML = `
      <input type="checkbox" ${allVisibleChecked ? "checked" : ""}
             class="cursor-pointer"
             style="width:15px; height:15px; accent-color:#3D5AFE;"
             data-action="change->dae20--workspace-export-map#toggleVisibleItems"
             data-group-key="${groupKey}"
             data-visible-codes="${filtered.map(it => it.code).join(",")}">
      <span>Код / Название</span>
      <span>Контекст</span>
    `

    const body = document.createElement("div")
    body.className = "flex-1 overflow-y-auto bg-white"

    if (filtered.length === 0) {
      body.innerHTML = `<div class="py-16 px-5 text-center text-[#6B7280] text-[13px]">Ничего не найдено.</div>`
    } else {
      filtered.forEach(item => body.appendChild(this._buildItemRow(groupKey, item, this.queries[groupKey])))
    }

    setTimeout(() => {
      const cb = tableHeader.querySelector("input[type=checkbox]")
      if (cb) cb.indeterminate = !allVisibleChecked && someVisible
    }, 0)

    wrap.appendChild(header)
    wrap.appendChild(tableHeader)
    wrap.appendChild(body)
    return wrap
  }

  _buildItemRow(groupKey, item, query) {
    const checked = this.selection[groupKey].has(item.code)

    // Контекстная инфа зависит от группы:
    //   components → page_code / parent_component_code / level
    //   sources → strategy_type + parent_folder_code
    //   item_templates → parent_folder_code
    //   folders → kind + parent_folder_code
    let context = ""
    if (groupKey === "components") {
      const parts = []
      if (item.component_level === "subcomponent" && item.parent_component_code) {
        parts.push(`<span class="text-[10.5px] text-[#9AA1AC]">sub of</span> <span class="font-mono text-[11px]">${this._esc(item.parent_component_code)}</span>`)
      } else if (item.page_code) {
        parts.push(`<span class="text-[10.5px] text-[#9AA1AC]">page</span> <span class="font-mono text-[11px]">${this._esc(item.page_code)}</span>`)
      }
      if (item.template_code) parts.push(`<span class="text-[10.5px] text-[#9AA1AC]">tpl</span> <span class="font-mono text-[11px]">${this._esc(item.template_code)}</span>`)
      context = parts.join(" · ")
    } else if (groupKey === "sources") {
      const parts = []
      if (item.strategy_type) parts.push(`<span class="font-mono text-[11px] text-[#6B7280]">${this._esc(item.strategy_type)}</span>`)
      if (item.parent_folder_code) parts.push(`<span class="text-[10.5px] text-[#9AA1AC]">folder</span> <span class="font-mono text-[11px]">${this._esc(item.parent_folder_code)}</span>`)
      context = parts.join(" · ")
    } else if (groupKey === "item_templates") {
      if (item.parent_folder_code) context = `<span class="text-[10.5px] text-[#9AA1AC]">folder</span> <span class="font-mono text-[11px]">${this._esc(item.parent_folder_code)}</span>`
    } else if (groupKey === "folders") {
      const parts = [`<span class="font-mono text-[11px] text-[#6B7280]">${this._esc(item.kind)}</span>`]
      if (item.parent_folder_code) parts.push(`<span class="text-[10.5px] text-[#9AA1AC]">parent</span> <span class="font-mono text-[11px]">${this._esc(item.parent_folder_code)}</span>`)
      context = parts.join(" · ")
    }

    const row = document.createElement("div")
    row.dataset.action = "click->dae20--workspace-export-map#toggleItemRow"
    row.dataset.groupKey = groupKey
    row.dataset.itemCode = item.code
    row.className = [
      "grid items-center gap-3 px-3.5 py-2.5 border-b border-[#EFF1F4] cursor-pointer transition-colors",
      checked ? "bg-[rgba(61,90,254,0.05)] border-l-2 border-l-[#3D5AFE]" : "border-l-2 border-l-transparent hover:bg-black/[0.025]"
    ].join(" ")
    row.style.gridTemplateColumns = "28px 1fr auto"

    row.innerHTML = `
      <div onclick="event.stopPropagation()">
        <input type="checkbox" ${checked ? "checked" : ""}
               class="cursor-pointer"
               style="width:15px; height:15px; accent-color:#3D5AFE;"
               data-action="change->dae20--workspace-export-map#toggleItemCheckbox"
               data-group-key="${groupKey}"
               data-item-code="${this._esc(item.code)}">
      </div>
      <div class="flex items-baseline gap-2.5 min-w-0">
        <span class="font-mono text-[12.5px] font-semibold whitespace-nowrap overflow-hidden text-ellipsis max-w-[45%]">
          ${this._highlight(item.code || "", query)}
        </span>
        <span class="text-[12.5px] text-[#6B7280] whitespace-nowrap overflow-hidden text-ellipsis">
          ${this._highlight(item.name || "", query)}
        </span>
      </div>
      <div class="text-right">${context}</div>
    `
    return row
  }

  _renderSelectionBar() {
    const count = this._totalSelected()
    if (count === 0) {
      this.selectionBarTarget.classList.add("hidden")
      this.selectionBarTarget.classList.remove("flex")
      return
    }
    this.selectionBarTarget.classList.remove("hidden")
    this.selectionBarTarget.classList.add("flex")
    this.selectionCountTarget.textContent = count

    // Разбивка: по группам.
    const parts = []
    GROUPS_ORDER.forEach(g => {
      const n = this.selection[g.key].size
      if (n > 0) parts.push(`<span class="font-mono">${n} ${g.key}</span>`)
    })
    if (this.workspaceSelection.size > 0) {
      parts.push(`<span class="font-mono">${this.workspaceSelection.size} workspace</span>`)
    }
    this.selectionBreakdownTarget.innerHTML = parts.join(" · ")
  }

  // ═════════════════════════════════════════════════════════════════
  // ACTIONS
  // ═════════════════════════════════════════════════════════════════

  setActiveGroup(event) {
    this.activeKey = event.currentTarget.dataset.groupKey
    localStorage.setItem("ex_active", this.activeKey)
    this._renderGroupsPane()
    this._renderDetail()
  }

  toggleItemRow(event) {
    const { groupKey, itemCode } = event.currentTarget.dataset
    const set = this.selection[groupKey]
    if (set.has(itemCode)) set.delete(itemCode); else set.add(itemCode)
    this._refresh(groupKey)
  }
  toggleItemCheckbox(event) {
    const { groupKey, itemCode } = event.target.dataset
    const set = this.selection[groupKey]
    if (event.target.checked) set.add(itemCode); else set.delete(itemCode)
    this._refresh(groupKey)
  }
  toggleWorkspaceBlock(event) {
    const id = event.currentTarget.dataset.blockId
    if (this.workspaceSelection.has(id)) this.workspaceSelection.delete(id)
    else this.workspaceSelection.add(id)
    this._refresh("workspace")
  }
  toggleWorkspaceCheckbox(event) {
    const id = event.target.dataset.blockId
    if (event.target.checked) this.workspaceSelection.add(id); else this.workspaceSelection.delete(id)
    this._refresh("workspace")
  }
  toggleVisibleItems(event) {
    const { groupKey } = event.target.dataset
    const codes = (event.target.dataset.visibleCodes || "").split(",").filter(Boolean)
    codes.forEach(c => {
      if (event.target.checked) this.selection[groupKey].add(c)
      else this.selection[groupKey].delete(c)
    })
    this._refresh(groupKey)
  }

  groupSelectAll(event)  { this._setGroupSelection(event.currentTarget.dataset.groupKey, () => true) }
  groupSelectNone(event) { this._setGroupSelection(event.currentTarget.dataset.groupKey, () => false) }

  workspaceSelectAll() {
    const all = ["workspace"]
    if (this.inventory.workspace_llm_settings_present) all.push("workspace_llm_settings")
    if (this.inventory.workspace_settings_present) all.push("workspace_settings")
    this.workspaceSelection = new Set(all)
    this._refresh("workspace")
  }
  workspaceSelectNone() {
    this.workspaceSelection = new Set()
    this._refresh("workspace")
  }

  onQueryChange(event) {
    const { groupKey } = event.target.dataset
    this.queries[groupKey] = event.target.value
    const input = event.target
    const pos = input.selectionStart
    this._renderDetail()
    const newInput = this.detailPaneTarget.querySelector(`input[data-group-key="${groupKey}"]`)
    if (newInput) {
      newInput.focus()
      try { newInput.setSelectionRange(pos, pos) } catch (e) {}
    }
  }

  clearSelection() {
    GROUPS_ORDER.forEach(g => this.selection[g.key] = new Set())
    this.workspaceSelection = new Set()
    this._renderAll()
  }

  invertSelection() {
    GROUPS_ORDER.forEach(g => {
      const items = this.inventory?.[g.key] || []
      const cur = this.selection[g.key]
      this.selection[g.key] = new Set(items.filter(it => !cur.has(it.code)).map(it => it.code))
    })
    const all = ["workspace"]
    if (this.inventory.workspace_llm_settings_present) all.push("workspace_llm_settings")
    if (this.inventory.workspace_settings_present) all.push("workspace_settings")
    this.workspaceSelection = new Set(all.filter(id => !this.workspaceSelection.has(id)))
    this._renderAll()
  }

  // ═════════════════════════════════════════════════════════════════
  // SUBMIT helpers
  // ═════════════════════════════════════════════════════════════════

  _syncHiddenSelectionInputs() {
    const form = this.element.querySelector("form")
    if (!form) return
    form.querySelectorAll('[data-selection-input="true"]').forEach(n => n.remove())

    GROUPS_ORDER.forEach(g => {
      // Marker — empty string, отфильтруется на бэке.
      form.appendChild(this._hiddenInput(`selection[${g.key}][]`, ""))
      this.selection[g.key].forEach(code => {
        form.appendChild(this._hiddenInput(`selection[${g.key}][]`, code))
      })
    })
    ;["workspace", "workspace_llm_settings", "workspace_settings"].forEach(key => {
      const checked = this.workspaceSelection.has(key)
      form.appendChild(this._hiddenInput(`selection[${key}]`, checked ? "true" : "false"))
    })
  }

  _hiddenInput(name, value) {
    const el = document.createElement("input")
    el.type = "hidden"
    el.name = name
    el.value = value
    el.dataset.selectionInput = "true"
    return el
  }

  // ═════════════════════════════════════════════════════════════════
  // HELPERS
  // ═════════════════════════════════════════════════════════════════

  _setGroupSelection(groupKey, predicate) {
    const items = this.inventory?.[groupKey] || []
    this.selection[groupKey] = new Set(items.filter(predicate).map(it => it.code))
    this._refresh(groupKey)
  }

  _refresh() {
    this._renderGroupsPane()
    this._renderTotalsCard()
    this._renderDetail()
    this._renderSelectionBar()
  }

  _computeTotals() {
    let total = 0
    GROUPS_ORDER.forEach(g => total += (this.inventory?.[g.key] || []).length)
    total += this._workspaceTotal()
    return { total }
  }

  _workspaceTotal() {
    if (!this.inventory) return 0
    let n = 1
    if (this.inventory.workspace_llm_settings_present) n++
    if (this.inventory.workspace_settings_present) n++
    return n
  }

  _totalSelected() {
    let n = this.workspaceSelection.size
    Object.values(this.selection).forEach(s => n += s.size)
    return n
  }

  _softBtnClass() {
    return "inline-flex items-center justify-center rounded-md h-7 px-2.5 text-[12px] font-semibold bg-[#EFF1F4] text-[#394150] cursor-pointer hover:bg-[#E6E8EC] transition-colors"
  }

  _highlight(text, q) {
    if (!q) return this._esc(text)
    const t = String(text)
    const idx = t.toLowerCase().indexOf(q.toLowerCase())
    if (idx < 0) return this._esc(t)
    return this._esc(t.slice(0, idx))
      + `<mark class="rounded-[3px] px-[1px]" style="background:rgba(245,158,11,.25); color:inherit;">${this._esc(t.slice(idx, idx + q.length))}</mark>`
      + this._esc(t.slice(idx + q.length))
  }

  _el(tag, className, html, attrs = {}) {
    const el = document.createElement(tag)
    if (className) el.className = className
    if (html) el.innerHTML = html
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v))
    return el
  }

  _esc(s) {
    return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }

  _showError(msg) {
    this.emptyStateTarget.classList.remove("hidden")
    this.groupsPaneTarget.classList.add("hidden")
    this.detailPaneTarget.classList.add("hidden")
    this.emptyStateTarget.innerHTML = `<div class="p-3 rounded bg-red-50 text-red-700 text-sm max-w-md">${this._esc(msg)}</div>`
  }
}
